import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';

import {
  createOfferCommission,
  deleteOfferCommission,
  getOfferCommissionsForVenue,
  updateOfferCommission,
} from 'apis/offer-commissions-service/api';
import {
  CreateOfferCommissionPayload,
  DeleteOfferCommissionPayload,
  OfferCommissionsServiceErrorResponse,
  UpdateOfferCommissionPayload,
} from 'apis/offer-commissions-service/types';
import useCurrentUser from 'common/hooks/useCurrentUser';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';

import { BasicDetails } from '../components/OfferDetailsForm/types';

type UseLoadVenueOfferCommissionsArgs = {
  enabled: boolean;
};

const QUERY_KEY_PREFIX = 'venue-offer-commissions';

export const useLoadVenueOfferCommissions = ({ enabled }: UseLoadVenueOfferCommissionsArgs) => {
  const scope = useScopeContext();
  const queryKey = useMemo(() => [QUERY_KEY_PREFIX, scope.id], [scope.id]);

  const { isInitialLoading } = useQuery(queryKey, () => getOfferCommissionsForVenue(scope.id), {
    enabled: !!enabled,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  });

  return {
    loadingCommissions: isInitialLoading,
  };
};

type CommissionFromValues = Pick<BasicDetails, 'eatinCommission' | 'takeawayCommission' | 'homedeliveryCommission'>;

type CreateAndUpdateCommissionArgs = {
  offerId: string;
  commissionFormValues: CommissionFromValues;
};

const errorIsOfferCommissionsServiceError = (error: any): error is OfferCommissionsServiceErrorResponse =>
  !!error?.problem?.message;

export const useVenueOfferCommissions = () => {
  const permissions = useOfferingMenuPermissionsContext();
  const scope = useScopeContext();
  const { vlmeConfig } = useSelectionInfoContext();
  const { data: userData } = useCurrentUser();
  const queryKey = useMemo(() => [QUERY_KEY_PREFIX, scope.id], [scope.id]);
  const queryClient = useQueryClient();

  const commissionsLoadedSuccessfully = useMemo(
    () => queryClient.getQueryState(queryKey)?.status === 'success',
    [queryClient, queryKey],
  );

  const enabled = useMemo(() => {
    return commissionsLoadedSuccessfully && permissions.customCommissionRead && !!vlmeConfig;
  }, [permissions, vlmeConfig, commissionsLoadedSuccessfully]);

  const {
    data,
    isLoading,
    refetch: refetchCommissions,
  } = useQuery(queryKey, () => getOfferCommissionsForVenue(scope.id), {
    enabled,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const getOfferCommissions = useCallback(
    (offerId: string) => {
      if (!data?.commissions) {
        return undefined;
      }

      return data.commissions.find(commission => commission.offer_id === offerId);
    },
    [data],
  );

  const { mutateAsync: executeCreateCommission } = useMutation(
    async (args: { payload: CreateOfferCommissionPayload }) => {
      await createOfferCommission(args.payload);
      await refetchCommissions();
    },
  );

  const { mutateAsync: executeUpdateCommission } = useMutation(
    async (args: { commissionId: string; payload: UpdateOfferCommissionPayload }) => {
      await updateOfferCommission(args.commissionId, args.payload);
      await refetchCommissions();
    },
  );

  const { mutateAsync: executeDeleteCommission } = useMutation(
    async (args: { commissionId: string; payload: DeleteOfferCommissionPayload }) => {
      await deleteOfferCommission(args.commissionId, args.payload);
      await refetchCommissions();
    },
  );

  const handleError = useCallback((error: any) => {
    if (error?.response?.data && !!errorIsOfferCommissionsServiceError(error.response.data)) {
      toast.error(error.response.data.problem.message, { duration: Infinity });
    } else {
      toast.error(<FormattedMessage id="errors.unknown-error-retry-contact" />, { duration: Infinity });
    }
  }, []);

  const createCommission = useCallback(
    async ({ offerId, commissionFormValues }: CreateAndUpdateCommissionArgs) => {
      if (!userData) {
        return Promise.reject();
      }

      const payload: CreateOfferCommissionPayload = {
        author: {
          id: userData?._id.$oid,
          kind: 'user',
        },
        commission: {
          offer_id: offerId,
          venue_id: scope.id,
          eatin: commissionFormValues.eatinCommission
            ? {
                percentage: commissionFormValues.eatinCommission,
              }
            : undefined,
          homedelivery: commissionFormValues.homedeliveryCommission
            ? {
                percentage: commissionFormValues.homedeliveryCommission,
              }
            : undefined,
          takeaway: commissionFormValues.takeawayCommission
            ? {
                percentage: commissionFormValues.takeawayCommission,
              }
            : undefined,
        },
      };

      try {
        await executeCreateCommission({ payload });
      } catch (e) {
        handleError(e);
      }
    },
    [executeCreateCommission, handleError, scope.id, userData],
  );
  const updateCommission = useCallback(
    async ({ offerId, commissionFormValues }: CreateAndUpdateCommissionArgs) => {
      const existingCommission = getOfferCommissions(offerId);

      if (!existingCommission || !userData) {
        return Promise.reject();
      }

      const payload: UpdateOfferCommissionPayload = {
        author: {
          id: userData?._id.$oid,
          kind: 'user',
        },
        version: existingCommission.version,
        eatin: commissionFormValues.eatinCommission
          ? {
              percentage: commissionFormValues.eatinCommission,
            }
          : undefined,
        homedelivery: commissionFormValues.homedeliveryCommission
          ? {
              percentage: commissionFormValues.homedeliveryCommission,
            }
          : undefined,
        takeaway: commissionFormValues.takeawayCommission
          ? {
              percentage: commissionFormValues.takeawayCommission,
            }
          : undefined,
      };

      try {
        await executeUpdateCommission({ commissionId: existingCommission.id, payload });
      } catch (e) {
        handleError(e);
      }
    },
    [executeUpdateCommission, getOfferCommissions, handleError, userData],
  );

  const deleteCommission = useCallback(
    async ({ offerId }: { offerId: string }) => {
      const existingCommission = getOfferCommissions(offerId);

      if (!existingCommission || !userData) {
        return Promise.reject();
      }

      const payload: DeleteOfferCommissionPayload = {
        author: {
          id: userData?._id.$oid,
          kind: 'user',
        },
      };

      try {
        await executeDeleteCommission({ commissionId: existingCommission.id, payload });
      } catch (e) {
        handleError(e);
      }
    },
    [executeDeleteCommission, getOfferCommissions, handleError, userData],
  );

  return {
    getOfferCommissions,
    loadingCommissions: isLoading,
    offerCommissions: data ?? [],
    createCommission,
    updateCommission,
    deleteCommission,
    commissionsLoadedSuccessfully,
  };
};
